import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { TrendingUp, TrendingDown, Minus, Plus, Trash2, RefreshCw, Target, ArrowUp, ArrowDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientRankTracker({ client, getHeaders }) {
  const { t } = useTranslation('dashboard');
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [adding, setAdding] = useState(false);
  const [selectedKw, setSelectedKw] = useState(null);

  const fetchKeywords = useCallback(() => {
    fetch(`${API}/api/client/rankings/keywords`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setKeywords(d.keywords || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getHeaders]);

  useEffect(() => { fetchKeywords(); }, [fetchKeywords]);

  const addKeyword = async (e) => {
    e.preventDefault();
    if (!newKeyword.trim()) return;
    setAdding(true);
    try {
      const res = await fetch(`${API}/api/client/rankings/keywords`, {
        method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ keyword: newKeyword.trim(), location: newLocation.trim() }),
      });
      if (res.ok) { setNewKeyword(''); setNewLocation(''); fetchKeywords(); }
      else { const d = await res.json(); alert(d.detail || t('rankTracker.addFailed')); }
    } catch (e) { console.error(e); }
    finally { setAdding(false); }
  };

  const deleteKeyword = async (id) => {
    if (!window.confirm(t('rankTracker.removeConfirm'))) return;
    await fetch(`${API}/api/client/rankings/keywords/${id}`, { method: 'DELETE', headers: getHeaders() });
    fetchKeywords();
  };

  const checkRankings = async () => {
    setChecking(true);
    try {
      const res = await fetch(`${API}/api/client/rankings/check`, {
        method: 'POST', headers: getHeaders(),
      });
      const d = await res.json();
      if (res.ok) { setKeywords(d.keywords || []); }
      else { alert(d.detail || t('rankTracker.checkFailed')); }
    } catch (e) { console.error(e); }
    finally { setChecking(false); }
  };

  if (loading) return <div className="text-gray-400 animate-pulse py-12 text-center">{t('rankTracker.loading')}</div>;

  const avgRank = keywords.filter(k => k.current_rank).length > 0
    ? (keywords.filter(k => k.current_rank).reduce((s, k) => s + k.current_rank, 0) / keywords.filter(k => k.current_rank).length).toFixed(1) : '—';
  const improved = keywords.filter(k => k.change > 0).length;
  const declined = keywords.filter(k => k.change < 0).length;

  return (
    <div className="space-y-6" data-testid="rank-tracker-tab">
      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-5 text-center">
            <Target className="h-5 w-5 text-blue-400 mx-auto mb-1" />
            <p className="text-xs text-gray-500 uppercase">{t('rankTracker.keywords')}</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{keywords.length}</p>
            <p className="text-xs text-gray-400 mt-1">{t('rankTracker.ofMax')}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-5 text-center">
            <p className="text-xs text-gray-500 uppercase">{t('rankTracker.avgPosition')}</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">#{avgRank}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-5 text-center">
            <ArrowUp className="h-5 w-5 text-green-400 mx-auto mb-1" />
            <p className="text-xs text-gray-500 uppercase">{t('rankTracker.improved')}</p>
            <p className="text-3xl font-bold text-green-400 mt-1">{improved}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-5 text-center">
            <ArrowDown className="h-5 w-5 text-red-400 mx-auto mb-1" />
            <p className="text-xs text-gray-500 uppercase">{t('rankTracker.declined')}</p>
            <p className="text-3xl font-bold text-red-400 mt-1">{declined}</p>
          </CardContent>
        </Card>
      </div>

      {/* Add Keyword + Check Rankings */}
      <div className="flex flex-wrap gap-3 items-end">
        <form onSubmit={addKeyword} className="flex gap-2 flex-1" data-testid="add-keyword-form">
          <input type="text" placeholder={t('rankTracker.keywordPlaceholder')} value={newKeyword}
            onChange={e => setNewKeyword(e.target.value)} required
            className="flex-1 bg-gray-50 border border-gray-200 text-gray-900 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            data-testid="keyword-input" />
          <input type="text" placeholder={t('rankTracker.locationPlaceholder')} value={newLocation}
            onChange={e => setNewLocation(e.target.value)}
            className="w-48 bg-gray-50 border border-gray-200 text-gray-900 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            data-testid="location-input" />
          <Button type="submit" disabled={adding} className="bg-blue-600 hover:bg-blue-700 text-white text-xs" data-testid="add-keyword-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> {adding ? t('rankTracker.addingShort') : t('rankTracker.add')}
          </Button>
        </form>
        <Button onClick={checkRankings} disabled={checking || keywords.length === 0}
          className="bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="check-rankings-btn">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${checking ? 'animate-spin' : ''}`} />
          {checking ? t('rankTracker.checking') : t('rankTracker.checkAll', { count: keywords.length })}
        </Button>
      </div>

      {/* Keywords Table */}
      <Card className="bg-white border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-4 py-3 text-left text-[10px] text-gray-500 uppercase">{t('rankTracker.tableKeyword')}</th>
                <th className="px-4 py-3 text-left text-[10px] text-gray-500 uppercase">{t('rankTracker.tableLocation')}</th>
                <th className="px-4 py-3 text-center text-[10px] text-gray-500 uppercase">{t('rankTracker.tablePosition')}</th>
                <th className="px-4 py-3 text-center text-[10px] text-gray-500 uppercase">{t('rankTracker.tableChange')}</th>
                <th className="px-4 py-3 text-center text-[10px] text-gray-500 uppercase">{t('rankTracker.tableBest')}</th>
                <th className="px-4 py-3 text-center text-[10px] text-gray-500 uppercase">{t('rankTracker.tableTrend')}</th>
                <th className="px-4 py-3 text-center text-[10px] text-gray-500 uppercase w-10" />
              </tr>
            </thead>
            <tbody>
              {keywords.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-gray-500 py-8">{t('rankTracker.emptyState')}</td></tr>
              ) : keywords.map(kw => {
                const change = kw.change || 0;
                const ChangeIcon = change > 0 ? TrendingUp : change < 0 ? TrendingDown : Minus;
                const changeColor = change > 0 ? 'text-green-400' : change < 0 ? 'text-red-400' : 'text-gray-500';
                const history = kw.history || [];
                const sparkMax = Math.max(...history.map(h => h.rank || 50), 50);
                const sparkMin = Math.min(...history.map(h => h.rank || 1), 1);
                const sparkRange = sparkMax - sparkMin || 1;

                return (
                  <tr key={kw.keyword_id} className="border-b border-gray-200/30 hover:bg-gray-100/20 transition-colors cursor-pointer"
                    onClick={() => setSelectedKw(selectedKw === kw.keyword_id ? null : kw.keyword_id)}>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-900 font-medium">{kw.keyword}</p>
                      {kw.last_checked && <p className="text-[10px] text-gray-500">{t('rankTracker.checkedOn', { date: new Date(kw.last_checked).toLocaleDateString() })}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">{kw.location}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-lg font-bold ${kw.current_rank ? (kw.current_rank <= 3 ? 'text-green-400' : kw.current_rank <= 10 ? 'text-blue-400' : kw.current_rank <= 20 ? 'text-yellow-400' : 'text-gray-400') : 'text-gray-600'}`}>
                        {kw.current_rank ? `#${kw.current_rank}` : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className={`flex items-center justify-center gap-1 ${changeColor}`}>
                        <ChangeIcon className="h-3.5 w-3.5" />
                        <span className="text-sm font-medium">{Math.abs(change)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-gray-400">
                      {kw.best_rank ? `#${kw.best_rank}` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {history.length > 1 && (
                        <svg viewBox={`0 0 ${Math.max(history.length - 1, 1) * 10} 24`} className="h-6 w-full max-w-[120px] mx-auto">
                          <polyline
                            fill="none"
                            stroke={change >= 0 ? '#22c55e' : '#ef4444'}
                            strokeWidth="1.5"
                            points={history.map((h, i) => `${i * 10},${24 - ((sparkMax - h.rank) / sparkRange) * 22}`).join(' ')}
                          />
                        </svg>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={(e) => { e.stopPropagation(); deleteKeyword(kw.keyword_id); }}
                        className="text-gray-600 hover:text-red-400 transition-colors" data-testid={`delete-kw-${kw.keyword_id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Expanded History Chart */}
      {selectedKw && (() => {
        const kw = keywords.find(k => k.keyword_id === selectedKw);
        if (!kw || !kw.history || kw.history.length < 2) return null;
        const h = kw.history;
        const maxR = Math.max(...h.map(p => p.rank));
        const minR = Math.min(...h.map(p => p.rank));
        const range = maxR - minR || 1;
        const chartH = 160;
        const chartW = 600;
        const stepX = chartW / (h.length - 1 || 1);

        return (
          <Card className="bg-white border-gray-200">
            <CardContent className="pt-5">
              <h3 className="text-gray-900 text-sm font-medium mb-3">{t('rankTracker.rankHistory', { keyword: kw.keyword })}</h3>
              <div className="overflow-x-auto">
                <svg viewBox={`-30 -10 ${chartW + 50} ${chartH + 30}`} className="w-full max-w-2xl mx-auto" style={{ height: 200 }}>
                  {/* Grid lines */}
                  {[0, 0.25, 0.5, 0.75, 1].map(pct => {
                    const y = chartH * pct;
                    const rankVal = Math.round(maxR - pct * range);
                    return (
                      <g key={pct}>
                        <line x1={0} y1={y} x2={chartW} y2={y} stroke="#1f2937" strokeWidth="1" />
                        <text x={-5} y={y + 4} textAnchor="end" fill="#6b7280" fontSize="10">#{rankVal}</text>
                      </g>
                    );
                  })}
                  {/* Line */}
                  <polyline
                    fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeLinejoin="round"
                    points={h.map((p, i) => `${i * stepX},${chartH - ((p.rank - minR) / range) * chartH}`).join(' ')}
                  />
                  {/* Dots */}
                  {h.map((p, i) => (
                    <circle key={i} cx={i * stepX} cy={chartH - ((p.rank - minR) / range) * chartH}
                      r="3" fill="#3b82f6" stroke="#111827" strokeWidth="1.5" />
                  ))}
                  {/* Labels */}
                  {h.filter((_, i) => i % Math.max(1, Math.floor(h.length / 6)) === 0 || i === h.length - 1).map((p, i, arr) => (
                    <text key={i} x={h.indexOf(p) * stepX} y={chartH + 16} textAnchor="middle" fill="#6b7280" fontSize="9">
                      {p.date ? new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                    </text>
                  ))}
                </svg>
              </div>
            </CardContent>
          </Card>
        );
      })()}
    </div>
  );
}
